import { useMemo, useState } from "react";
import { describeError, isApiError } from "@/api/client";
import type { Workspace } from "@/api/session";
import { useUpdateWorkspace } from "@/api/workspace";
import {
  Button,
  Form,
  FormActions,
  FormSection,
  InlineAlert,
  Select,
  TextField,
  UnsavedChangesGuard,
  useToast,
} from "@/components";
import { COUNTRIES, countryName, currencyChoices, isCanadianPostalCode, regionsFor, type Choice } from "@/lib/regions";

interface WorkspaceFormValues {
  name: string;
  line1: string;
  city: string;
  region: string;
  postal_code: string;
  country: string;
  market_country: string;
  currency: string;
  compliance_market: string;
}

type FieldErrors = Partial<Record<keyof WorkspaceFormValues, string>>;

const COMPLIANCE_MARKETS: Choice[] = [
  { value: "CA", label: "Canada" },
  { value: "EU", label: "European Union" },
  { value: "US", label: "United States" },
  { value: "GB", label: "United Kingdom" },
];

function valuesFrom(workspace: Workspace): WorkspaceFormValues {
  return {
    name: workspace.name,
    line1: workspace.ship_to.line1,
    city: workspace.ship_to.city,
    region: workspace.ship_to.region,
    postal_code: workspace.ship_to.postal_code,
    country: workspace.ship_to.country || "CA",
    market_country: workspace.market_country,
    currency: workspace.currency,
    compliance_market: workspace.compliance_market,
  };
}

function validate(values: WorkspaceFormValues): FieldErrors {
  const errors: FieldErrors = {};
  if (!values.name.trim()) errors.name = "Give the workspace a name.";
  if (!values.country) errors.country = "Choose the country orders are delivered to.";
  if (values.country === "CA" && values.postal_code.trim() && !isCanadianPostalCode(values.postal_code)) {
    errors.postal_code = "Canadian postal codes look like V6B 1A1.";
  }
  return errors;
}

/** Server field paths (Ninja validation) mapped to form fields. */
const SERVER_FIELDS: Record<string, keyof WorkspaceFormValues> = {
  name: "name",
  "ship_to.line1": "line1",
  "ship_to.city": "city",
  "ship_to.region": "region",
  "ship_to.postal_code": "postal_code",
  "ship_to.country": "country",
  market_country: "market_country",
  currency: "currency",
  compliance_market: "compliance_market",
};

export function WorkspaceSection({ workspace, canEdit }: { workspace: Workspace; canEdit: boolean }) {
  const initial = useMemo(() => valuesFrom(workspace), [workspace]);
  const [values, setValues] = useState(initial);
  const [clientErrors, setClientErrors] = useState<FieldErrors>({});
  const update = useUpdateWorkspace();
  const toast = useToast();

  const dirty = (Object.keys(initial) as Array<keyof WorkspaceFormValues>).some((key) => values[key] !== initial[key]);
  const serverErrors: FieldErrors = {};
  if (isApiError(update.error)) {
    for (const [path, message] of Object.entries(update.error.fieldErrors)) {
      const field = SERVER_FIELDS[path];
      if (field) serverErrors[field] = message;
    }
  }
  const unmappedError = update.isError && Object.keys(serverErrors).length === 0;
  const errorFor = (field: keyof WorkspaceFormValues) => clientErrors[field] ?? serverErrors[field] ?? null;

  function set<K extends keyof WorkspaceFormValues>(field: K) {
    return (value: string | null) => setValues((current) => ({ ...current, [field]: value ?? "" }));
  }

  function changeCountry(country: string | null) {
    setValues((current) => {
      const regions = regionsFor(country ?? "");
      const regionStillValid = !regions || regions.some((region) => region.value === current.region);
      return { ...current, country: country ?? "", region: regionStillValid ? current.region : "" };
    });
  }

  function handleSubmit() {
    const errors = validate(values);
    setClientErrors(errors);
    if (Object.keys(errors).length) return;
    update.mutate(
      {
        name: values.name.trim(),
        ship_to: {
          line1: values.line1.trim(),
          city: values.city.trim(),
          region: values.region.trim(),
          postal_code: values.postal_code.trim().toUpperCase(),
          country: values.country,
        },
        market_country: values.market_country,
        currency: values.currency,
        compliance_market: values.compliance_market,
      },
      {
        onSuccess: (saved) => {
          setValues(valuesFrom(saved));
          toast.success("Workspace settings saved");
        },
      },
    );
  }

  const regionChoices = regionsFor(values.country);
  const complianceChoices = COMPLIANCE_MARKETS.some((market) => market.value === values.compliance_market)
    ? COMPLIANCE_MARKETS
    : [...COMPLIANCE_MARKETS, { value: values.compliance_market, label: values.compliance_market }];
  const disabled = !canEdit;

  return (
    <Form onSubmit={handleSubmit} aria-label="Workspace settings">
      <UnsavedChangesGuard when={dirty && canEdit} subject="your workspace changes" />
      {unmappedError ? (
        <InlineAlert tone="danger" title="Couldn’t save the workspace settings">
          {describeError(update.error)}
        </InlineAlert>
      ) : null}

      <FormSection title="Workspace">
        <TextField
          label="Workspace name"
          name="name"
          value={values.name}
          onValueChange={set("name")}
          error={errorFor("name")}
          disabled={disabled}
          autoComplete="organization"
        />
      </FormSection>

      <FormSection
        title="Ship-to address"
        description="Where orders are delivered. Supplier availability, shipping costs and duty are worked out for this address."
      >
        <Select
          label="Country"
          name="country"
          options={COUNTRIES}
          value={values.country}
          onValueChange={changeCountry}
          error={errorFor("country")}
          disabled={disabled}
        />
        {regionChoices ? (
          <Select
            label={values.country === "CA" ? "Province or territory" : "State"}
            name="region"
            options={regionChoices}
            value={values.region || null}
            onValueChange={set("region")}
            placeholder="Choose…"
            error={errorFor("region")}
            disabled={disabled}
          />
        ) : (
          <TextField
            label="Region"
            name="region"
            optional
            value={values.region}
            onValueChange={set("region")}
            error={errorFor("region")}
            disabled={disabled}
            autoComplete="address-level1"
          />
        )}
        <TextField
          label="City"
          name="city"
          value={values.city}
          onValueChange={set("city")}
          error={errorFor("city")}
          disabled={disabled}
          autoComplete="address-level2"
        />
        <TextField
          label="Postal code"
          name="postal_code"
          value={values.postal_code}
          onValueChange={set("postal_code")}
          error={errorFor("postal_code")}
          disabled={disabled}
          autoComplete="postal-code"
          mono
          placeholder={values.country === "CA" ? "V6B 1A1" : undefined}
        />
        <TextField
          label="Address line"
          name="line1"
          optional
          value={values.line1}
          onValueChange={set("line1")}
          error={errorFor("line1")}
          disabled={disabled}
          autoComplete="address-line1"
        />
      </FormSection>

      <FormSection
        title="Market and compliance"
        description="Prices, costs and regulatory checks follow these choices."
      >
        <Select
          label="Price market"
          name="market_country"
          options={COUNTRIES}
          value={values.market_country}
          onValueChange={set("market_country")}
          description={`Supplier prices are fetched as offered in ${countryName(values.market_country || "CA")}.`}
          error={errorFor("market_country")}
          disabled={disabled}
        />
        <Select
          label="Currency"
          name="currency"
          options={currencyChoices(values.currency)}
          value={values.currency}
          onValueChange={set("currency")}
          description="Prices, costs and baskets are shown in this currency."
          error={errorFor("currency")}
          disabled={disabled}
        />
        <Select
          label="Compliance market"
          name="compliance_market"
          options={complianceChoices}
          value={values.compliance_market}
          onValueChange={set("compliance_market")}
          description="Decides which allergen labelling rules apply. IFRA limits apply everywhere."
          error={errorFor("compliance_market")}
          disabled={disabled}
        />
      </FormSection>

      <FormActions>
        <Button
          type="submit"
          variant="primary"
          pending={update.isPending}
          pendingLabel="Saving…"
          disabled={disabled || !dirty}
        >
          Save workspace
        </Button>
        {dirty ? (
          <Button
            variant="ghost"
            onClick={() => {
              setValues(initial);
              setClientErrors({});
              update.reset();
            }}
          >
            Discard changes
          </Button>
        ) : null}
      </FormActions>
    </Form>
  );
}
